import type { AnalyticsRange, StaffAnalyticsOverview } from "@lafayette/schemas";
import { screenLabel } from "../explore/metric-catalog";
import { ShareBar } from "../explore/ShareBar";
import { countText, durationText } from "../format";
import { rangePhrase } from "../overview-model";
import { Tile } from "./KpiTiles";
import { CardShell } from "./OverviewCards";

// The app-activity cards of the Overview (ADR 0017, decision 9.1; CHT-2029): the Sessions tile, the "Time in app" tile and "Where the time went".
// They read the `app` part of the Overview answer: the visits of the window from the derived visit read model, in the same gate transaction as the
// records. The part is ABSENT when the customer opted out or analytics is off; the cards then draw nothing: the notice at the top of the Overview says why. Each card opens
// Explore with its metric. NOT built, on purpose: "When active" (the heatmap) and "Devices and sessions" (device models are out of scope).

type Overview = StaffAnalyticsOverview;

export function AppActivityCards({
  overview,
  range,
}: Readonly<{ overview: Overview; range: AnalyticsRange }>) {
  const { app } = overview;
  const phrase = rangePhrase(range);
  // Without the app part the notice at the top of the Overview says why (`app-data-words.ts`); the cards add no second sentence.
  if (app === undefined) return null;
  const total = app.whereTimeWent.reduce((sum, row) => sum + row.seconds, 0);
  return (
    <div className="grid gap-4 lg:col-span-3 lg:grid-cols-3">
      <ul className="m-0 grid list-none content-start gap-4 p-0">
        <li>
          <Tile
            label="Sessions"
            value={countText(app.sessions)}
            detail={`Visits to the app that started ${phrase}`}
            metric="sessions"
            range={range}
          />
        </li>
        <li>
          <Tile
            label="Time in app"
            value={durationText(app.timeInAppSeconds)}
            detail={`Counted by the start of each visit, ${phrase}`}
            metric="time_in_app"
            range={range}
          />
        </li>
      </ul>
      <CardShell
        title="Where the time went"
        description={`Time in the app by screen, ${phrase}`}
        className="lg:col-span-2"
        zoom={{ metric: "screen_time", range, split: "screen" }}
      >
        {total === 0 ? (
          <p className="m-0 text-sm text-muted-foreground">No time in the app {phrase}.</p>
        ) : (
          <ul className="m-0 grid list-none gap-2.5 p-0">
            {app.whereTimeWent.map((row) => (
              <ShareBar
                key={row.screen}
                label={screenLabel(row.screen)}
                share={row.seconds / total}
              />
            ))}
          </ul>
        )}
      </CardShell>
    </div>
  );
}
